import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { categories } from "@/lib/toolsData";

const inputCls = "w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500";
const empty = () => ({ name: "", affiliate_url: "", image_url: "", description: "", button_text: "View Deal", commission_notes: "", category: "", priority: 0, enabled: true, status: "active" });

export const AffiliatesManager = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);

  const load = useCallback(async () => {
    try { const r = await adminApi.get("/admin/affiliates"); setItems(r.data.affiliates); }
    catch { toast.error("Could not load affiliates"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const save = async () => {
    if (!editing.name.trim() || !editing.affiliate_url.trim()) return toast.error("Name and affiliate URL are required");
    try {
      if (isNew) await adminApi.post("/admin/affiliates", editing);
      else await adminApi.put(`/admin/affiliates/${editing.id}`, editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await adminApi.delete(`/admin/affiliates/${id}`); load(); };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button data-testid="aff-new" onClick={() => { setEditing(empty()); setIsNew(true); }} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700"><Plus className="h-4 w-4" /> New Product</button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.length === 0 && <p className="col-span-full py-8 text-center text-sm text-neutral-400">No affiliate products yet.</p>}
        {items.map((a) => (
          <div key={a.id} data-testid={`aff-card-${a.id}`} className="rounded-xl border border-neutral-200 bg-white p-4">
            <div className="flex items-start gap-3">
              {a.image_url && <img src={a.image_url} alt={a.name} className="h-12 w-12 rounded-lg object-cover" />}
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold">{a.name}</p>
                <p className="text-xs text-neutral-500">{a.category || "all categories"} · {a.clicks || 0} clicks</p>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${a.enabled ? "bg-green-100 text-green-700" : "bg-neutral-200 text-neutral-500"}`}>{a.enabled ? "Live" : "Off"}</span>
            </div>
            <div className="mt-3 flex gap-1.5">
              <button onClick={() => { setEditing(a); setIsNew(false); }} className="rounded p-1.5 hover:bg-neutral-100"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(a.id)} className="rounded p-1.5 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/50 p-4">
          <div className="my-8 w-full max-w-lg space-y-3 rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between"><h3 className="font-heading text-lg font-bold">{isNew ? "New" : "Edit"} Affiliate Product</h3><button onClick={() => setEditing(null)}><X className="h-5 w-5" /></button></div>
            <input data-testid="aff-name" className={inputCls} placeholder="Product name" value={editing.name} onChange={(e) => set("name", e.target.value)} />
            <input data-testid="aff-url" className={inputCls} placeholder="Affiliate URL" value={editing.affiliate_url} onChange={(e) => set("affiliate_url", e.target.value)} />
            <input className={inputCls} placeholder="Image URL" value={editing.image_url} onChange={(e) => set("image_url", e.target.value)} />
            <textarea className={inputCls} rows={2} placeholder="Description" value={editing.description} onChange={(e) => set("description", e.target.value)} />
            <div className="grid grid-cols-2 gap-3">
              <input className={inputCls} placeholder="Button text" value={editing.button_text} onChange={(e) => set("button_text", e.target.value)} />
              <input className={inputCls} type="number" placeholder="Priority" value={editing.priority} onChange={(e) => set("priority", Number(e.target.value))} />
            </div>
            <select className={inputCls} value={editing.category} onChange={(e) => set("category", e.target.value)}>
              <option value="">All categories</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <input className={inputCls} placeholder="Commission notes (internal)" value={editing.commission_notes} onChange={(e) => set("commission_notes", e.target.value)} />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.enabled} onChange={(e) => set("enabled", e.target.checked)} /> Enabled</label>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setEditing(null)} className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-semibold">Cancel</button>
              <button data-testid="aff-save" onClick={save} className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-bold text-white hover:bg-blue-700">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
